import type { CatalogFieldState, CatalogOption } from "@/lib/api/generated/casedb";
import type { CaseFieldEvidence, CaseFieldOption, ReviewDecision } from "../model/caseReview";
import { REVIEW_DECISIONS, REVIEW_STATUSES } from "../constants/caseReviewDecisions";
import { RECORD_ID_PROPERTY } from "../constants/catalogRecordIdentity";

/**
 * What a section's stored review state means to the controls that render it.
 *
 * The server keys everything by catalog field id and the form keys everything by control key, so
 * every function here takes the translation as `keyOf`. A scalar section resolves it through the
 * schema's `x-field-id`; a record resolves it by stripping the collection prefix. That is the only
 * difference between the two, and it used to be the reason both had their own copy of all of this.
 */

const CONFIDENCE = /^(H|M|L|HIGH|MEDIUM|LOW)$/i;

/** Field id to control key; undefined for a field id this form does not show. */
export type FieldKeyResolver = (fieldId: string) => string | undefined;

/** The server's H/M/L (or spelled-out) confidence, normalized to the form the UI renders. */
export function normalizeConfidence(value: string): CaseFieldEvidence["confidence"] {
  const normalized = value.trim().toUpperCase();
  if (normalized === "H" || normalized === "HIGH") return "high";
  if (normalized === "L" || normalized === "LOW") return "low";
  return "medium";
}

/** Adapts the original `<record>|<field>` section map into one record's field-id map. */
export function statesForRecord(states: Record<string, CatalogFieldState>, recordId: string): Record<string, CatalogFieldState> {
  const prefix = `${recordId}|`;
  return Object.fromEntries(Object.entries(states).flatMap(([key, state]) =>
    key.startsWith(prefix) ? [[key.slice(prefix.length), state]] : []));
}

/** Review identity, not confidence, determines whether an extracted value has review actions. */
export function hasReviewRecord(state: CatalogFieldState | undefined): boolean {
  return state?.review_id != null;
}

/** Keep review actions visible even when extraction did not supply a confidence score. */
export function hasAiReviewColumns(evidence: Record<string, CaseFieldEvidence>,
                                   states: Record<string, CatalogFieldState> = {}): boolean {
  return Object.keys(evidence).length > 0 || Object.values(states).some(hasReviewRecord);
}

/** The graded confidence of one field state, or undefined when it carries none. */
export function confidence(state: CatalogFieldState | undefined): CaseFieldEvidence["confidence"] | undefined {
  if (!state?.confidence || !CONFIDENCE.test(state.confidence.trim())) return undefined;
  return normalizeConfidence(state.confidence);
}

/** The AI evidence of every field that has some, keyed the way the form keys its controls. */
export function evidenceOf(states: Record<string, CatalogFieldState>, keyOf: FieldKeyResolver): Record<string, CaseFieldEvidence> {
  return Object.fromEntries(Object.entries(states).flatMap(([fieldId, state]) => {
    const level = confidence(state);
    const key = keyOf(fieldId);
    if (!level || !key) return [];
    const provenance = state.provenance as { source_text?: string } | null | undefined;
    return [[key, {
      confidence: level,
      quote: provenance?.source_text ?? state.evidence?.[0] ?? "Source evidence available.",
      reasoning: state.reasoning ?? "",
    }]];
  }));
}

/** The decisions a reviewer has already saved, as the draft records them. */
export function decisionsFromStates(states: Record<string, CatalogFieldState>, keyOf: FieldKeyResolver): Record<string, ReviewDecision> {
  const decisions: Record<string, ReviewDecision> = {};
  Object.entries(states).forEach(([fieldId, state]) => {
    const key = keyOf(fieldId);
    if (!key) return;
    const status = state.review_status?.toLowerCase();
    if (status === REVIEW_STATUSES.accepted) decisions[key] = REVIEW_DECISIONS.accepted;
    if (status === REVIEW_STATUSES.corrected || status === REVIEW_STATUSES.edited) decisions[key] = REVIEW_DECISIONS.corrected;
    if (status === REVIEW_STATUSES.rejected) decisions[key] = REVIEW_DECISIONS.rejected;
  });
  return decisions;
}

interface SchemaWithProperties {
  properties?: Record<string, unknown>;
}

/**
 * The options each coded control offers, keyed by codelist source.
 *
 * The controls ask by source name because that is what the schema puts on the property; the server
 * answers by field id. This is where the two meet.
 */
export function optionsOf(schema: SchemaWithProperties | undefined, fieldOptions: Record<string, CatalogOption[]>): Record<string, CaseFieldOption[]> {
  const entries = (properties: Record<string, unknown>): Array<[string, CaseFieldOption[]]> =>
    Object.entries(properties).flatMap(([key, raw]) => {
    const property = raw as Record<string, unknown>;
    const source = property["x-codelist"];
    if (typeof source !== "string") {
      const nested = property.properties;
      return nested && typeof nested === "object"
        ? entries(nested as Record<string, unknown>) : [];
    }
    const fieldId = String(property["x-field-id"] ?? key);
    const list = fieldOptions[fieldId] ?? fieldOptions[source] ?? [];
    return [[source, list.flatMap((item) => item.id != null && item.label != null
      ? [{ id: item.id, label: item.label, active: item.active }] : [])]];
  });
  const resolved: Record<string, CaseFieldOption[]> = {};
  entries(schema?.properties ?? {}).forEach(([source, options]) => {
    const existing = resolved[source] ?? [];
    const seen = new Set(existing.map((option) => String(option.id)));
    resolved[source] = [...existing, ...options.filter((option) => !seen.has(String(option.id)))];
  });
  return resolved;
}

/** How many fields one record of a collection shows; its identity is not one of them. */
export function recordFieldCount(itemSchema: SchemaWithProperties | undefined, recordIdProperty: string = RECORD_ID_PROPERTY): number {
  let count = 0;
  for (const [key, raw] of Object.entries(itemSchema?.properties ?? {})) {
    const property = raw as SchemaWithProperties & { type?: string; "x-field-id"?: string };
    if (key === recordIdProperty || property.type === "array") continue;
    count += property.type === "object" && !property["x-field-id"]
      ? recordFieldCount(property, recordIdProperty) : 1;
  }
  return count;
}
